import React from 'react';

interface LegacyBallNextStepProps {
  hasRegistration: boolean;
  hasTicket: boolean;
  ticketStatus: 'Active' | 'Cancelled' | null;
  isCheckedIn: boolean;
}

/**
 * Displays the user's current position in the Legacy Ball journey
 * and indicates what their next step is.
 */
export function LegacyBallNextStep({ hasRegistration, hasTicket, ticketStatus, isCheckedIn }: LegacyBallNextStepProps) {
  // Determine the current step and next action
  let stepIndex = 0;
  let nextStepMessage = '';
  let nextStepIcon = '🎫';

  if (!hasRegistration && !hasTicket) {
    stepIndex = 0;
    nextStepMessage = 'Next step for you: Purchase Ticket → Register';
    nextStepIcon = '🛒';
  } else if (hasRegistration && !hasTicket) {
    stepIndex = 1;
    nextStepMessage = 'Next step for you: Purchase your Legacy Ball Ticket';
    nextStepIcon = '🎫';
  } else if (hasTicket && ticketStatus === 'Active' && !isCheckedIn) {
    stepIndex = 2;
    nextStepMessage = 'Next step for you: Check in at the Legacy Ball';
    nextStepIcon = '✅';
  } else if (isCheckedIn) {
    stepIndex = 3;
    nextStepMessage = 'You\'re checked in! Enjoy the Legacy Ball 🎉';
    nextStepIcon = '🎉';
  } else if (ticketStatus === 'Cancelled') {
    stepIndex = 0;
    nextStepMessage = 'Your ticket was cancelled. Please contact the organizer.';
    nextStepIcon = '⚠️';
  }

  const steps = [
    { icon: '🛒', label: 'Purchase Ticket' },
    { icon: '📝', label: 'Register' },
    { icon: '✅', label: 'Check In' },
    { icon: '🎉', label: 'Enjoy!' },
  ];

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <span style={styles.headerIcon}>🎭</span>
        <span style={styles.headerTitle}>Legacy Ball Journey</span>
      </div>

      {/* Progress Steps */}
      <div style={styles.stepsRow}>
        {steps.map((step, idx) => {
          const isCompleted = idx < stepIndex;
          const isCurrent = idx === stepIndex && stepIndex < 3;
          const isLast = idx === 3 && stepIndex === 3;
          return (
            <React.Fragment key={idx}>
              <div style={styles.stepItem}>
                <div style={{
                  ...styles.stepDot,
                  backgroundColor: isCompleted || isLast ? '#1a6b3c' : isCurrent ? '#fff' : '#e0e0e0',
                  borderColor: isCompleted || isLast ? '#1a6b3c' : isCurrent ? '#1a6b3c' : '#ccc',
                  color: isCompleted || isLast ? '#fff' : isCurrent ? '#1a6b3c' : '#999',
                }}>
                  {isCompleted ? '✓' : step.icon}
                </div>
                <span style={{
                  ...styles.stepLabel,
                  color: isCompleted || isCurrent || isLast ? '#0f2d1f' : '#999',
                  fontWeight: isCurrent ? 700 : 500,
                }}>
                  {step.label}
                </span>
              </div>
              {idx < steps.length - 1 && (
                <div style={{
                  ...styles.connector,
                  backgroundColor: isCompleted ? '#1a6b3c' : '#e0e0e0',
                }} />
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* Next Step Message */}
      <div style={styles.messageBox}>
        <span style={styles.messageIcon}>{nextStepIcon}</span>
        <span style={styles.messageText}>{nextStepMessage}</span>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    backgroundColor: '#fff',
    border: '1px solid #e0e0e0',
    borderRadius: '12px',
    padding: '1.25rem',
    marginBottom: '1.25rem',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '1rem',
  },
  headerIcon: {
    fontSize: '1.25rem',
  },
  headerTitle: {
    fontSize: '1rem',
    fontWeight: 700,
    color: '#0f2d1f',
  },
  stepsRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0',
    marginBottom: '1rem',
    flexWrap: 'wrap',
  },
  stepItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '6px',
    minWidth: '70px',
  },
  stepDot: {
    width: '36px',
    height: '36px',
    borderRadius: '50%',
    border: '2px solid',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '14px',
    fontWeight: 700,
  },
  stepLabel: {
    fontSize: '11px',
    textAlign: 'center',
  },
  connector: {
    width: '40px',
    height: '3px',
    borderRadius: '2px',
    marginBottom: '20px',
  },
  messageBox: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '10px 14px',
    backgroundColor: '#f0faf4',
    border: '1px solid #c8e6c9',
    borderRadius: '8px',
  },
  messageIcon: {
    fontSize: '1.25rem',
  },
  messageText: {
    fontSize: '0.875rem',
    fontWeight: 600,
    color: '#1a6b3c',
  },
};
